import { useTranslation } from 'react-i18next';
import { FileText, FileVideo, Image as ImageIcon } from 'lucide-react';
import {
  AssistantMessageRole,
  AssistantMessageStatus,
  AssistantUploadKind,
  type AssistantAttachmentPart,
  type AssistantDecision,
  type AssistantMessage,
} from '@xtreed/academy-shared';
import { cn } from '@/lib/utils';
import type { AssistantUploadView } from '@/hooks/assistant/useAssistantUploads';
import { AssistantMarkdown } from './AssistantMarkdown';
import { ApprovalCard } from './ApprovalCard';
import { ToolCallChip } from './ToolCallChip';
import { UploadStatusBadge } from './UploadStatusBadge';

const KIND_ICON = {
  [AssistantUploadKind.VIDEO]: FileVideo,
  [AssistantUploadKind.IMAGE]: ImageIcon,
  [AssistantUploadKind.MATERIAL]: FileText,
};

function AttachmentChip({
  part,
  upload,
}: {
  part: AssistantAttachmentPart;
  upload?: AssistantUploadView;
}) {
  const Icon = KIND_ICON[part.kind] ?? FileText;
  return (
    <div className="flex items-center gap-2 rounded-lg bg-black/15 px-2 py-1.5 text-xs">
      <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" />
      <span className="min-w-0 flex-1 truncate">{part.filename}</span>
      {upload && (
        <UploadStatusBadge
          status={upload.status}
          progress={upload.local?.progress}
          className="text-current opacity-90"
        />
      )}
    </div>
  );
}

interface MessageBubbleProps {
  message: AssistantMessage;
  uploadsById: Map<string, AssistantUploadView>;
  isRunning: boolean;
  enterDelayMs?: number;
  onDecide: (interruptId: string, decisions: AssistantDecision[]) => void;
}

const bubbleRadius = (isUser: boolean) => ({
  borderTopLeftRadius: 'var(--bubble-radius)',
  borderTopRightRadius: 'var(--bubble-radius)',
  borderBottomLeftRadius: isUser
    ? 'var(--bubble-radius)'
    : 'var(--bubble-tail)',
  borderBottomRightRadius: isUser
    ? 'var(--bubble-tail)'
    : 'var(--bubble-radius)',
});

export function MessageBubble({
  message,
  uploadsById,
  isRunning,
  enterDelayMs = 0,
  onDecide,
}: MessageBubbleProps) {
  const { t } = useTranslation('adminAssistant');
  const isUser = message.role === AssistantMessageRole.USER;
  const animationDelay = enterDelayMs ? `${enterDelayMs}ms` : undefined;

  if (isUser) {
    const texts = message.parts.filter((part) => part.type === 'text');
    const attachments = message.parts.filter(
      (part): part is AssistantAttachmentPart => part.type === 'attachment',
    );
    return (
      <li data-message-id={message.id} className="flex justify-end">
        <div
          className="bubble-in max-w-[85%] space-y-1.5 bg-primary px-3 py-2 text-sm leading-relaxed text-primary-foreground"
          style={{ ...bubbleRadius(true), animationDelay }}
        >
          {texts.map((part, index) =>
            part.type === 'text' ? (
              <p key={index} className="wrap-break-word whitespace-pre-wrap">
                {part.text}
              </p>
            ) : null,
          )}
          {attachments.map((part) => (
            <AttachmentChip
              key={part.uploadId}
              part={part}
              upload={uploadsById.get(part.uploadId)}
            />
          ))}
        </div>
      </li>
    );
  }

  return (
    <li
      data-message-id={message.id}
      className="flex flex-col items-start gap-2"
      style={{ animationDelay }}
    >
      {message.parts.map((part, index) => {
        if (part.type === 'text') {
          return (
            <div
              key={index}
              className="bubble-in max-w-[92%] border border-border/40 bg-secondary/60 px-3 py-2 text-sm leading-relaxed text-foreground"
              style={bubbleRadius(false)}
            >
              <AssistantMarkdown text={part.text} />
            </div>
          );
        }
        if (part.type === 'tool_call') {
          return <ToolCallChip key={part.id} part={part} />;
        }
        if (part.type === 'approval') {
          return (
            <ApprovalCard
              key={part.interruptId}
              part={part}
              disabled={isRunning}
              onDecide={(decisions) => onDecide(part.interruptId, decisions)}
            />
          );
        }
        return null;
      })}
      {(message.status === AssistantMessageStatus.ABORTED ||
        message.status === AssistantMessageStatus.ERROR) && (
        <p
          className={cn(
            'px-1 text-[11px]',
            message.status === AssistantMessageStatus.ERROR
              ? 'text-destructive'
              : 'text-muted-foreground',
          )}
        >
          {message.status === AssistantMessageStatus.ERROR
            ? t('message.error')
            : t('message.aborted')}
        </p>
      )}
    </li>
  );
}
